import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DomainService, DomainType } from './domain.service';
import { AuthService } from './auth.service';
import { UserRoleType } from '../../shared/interfaces/auth.interfaces';

export interface NavigationItem {
  label: string;
  route: string;
  icon?: string;
  children?: NavigationItem[];
  requiredUserType?: UserRoleType;
  requiredAccessLevel?: 'admin' | 'user' | 'in-house';
  requiredDomain?: DomainType[];
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private baseNavigationItems: NavigationItem[] = [
    {
      label: 'nav.welcome',
      route: '/main/welcome',
      icon: 'fas fa-home'
    },
    {
      label: 'nav.inventory',
      route: '/main/inventory',
      icon: 'fas fa-boxes',
      requiredUserType: 'Media Owners',
      requiredDomain: ['ssp', 'localhost'],
      children: [
        { label: 'nav.inventory.manage', route: '/main/inventory/manage' },
        // { label: 'Need Attention', route: '/main/inventory/need-attention' },
        { label: 'nav.inventory.bookings', route: '/main/inventory/bookings' },
        { label: 'nav.inventory.performance', route: '/main/inventory/performance' },
        { label: 'nav.inventory.availability', route: '/main/inventory/availability' },
        { label: 'nav.inventory.pricing', route: '/main/inventory/pricing' },
        { label: 'nav.inventory.audience', route: '/main/inventory/audience' }
      ]
    },
    {
      label: 'nav.campaign',
      route: '/main/campaign',
      icon: 'fas fa-bullhorn',
      requiredUserType: 'Advertisers',
      requiredDomain: ['dsp', 'localhost']
    }
  ];

  private domainService = inject(DomainService);
  private authService = inject(AuthService);
  private router = inject(Router);

  getNavigationItems(): NavigationItem[] {
    const currentDomain = this.domainService.getCurrentDomain();
    const userType = this.authService.getUserType();
    const accessLevel = this.authService.getAccessLevel();
    
    return this.baseNavigationItems.filter(item => 
      this.isItemAccessible(item, currentDomain, userType, accessLevel)
    ).map(item => ({
      ...item,
      children: item.children?.filter(child => 
        this.isItemAccessible(child, currentDomain, userType, accessLevel)
      )
    }));
  }

  private isItemAccessible(
    item: NavigationItem, 
    currentDomain: DomainType, 
    userType: UserRoleType | null,
    accessLevel: 'admin' | 'user' | 'in-house' | null
  ): boolean {
    // Admin dan in-house dapat mengakses semua menu
    if (accessLevel === 'admin' || accessLevel === 'in-house') {
      return true;
    }

    // Cek domain requirement
    if (item.requiredDomain && !item.requiredDomain.includes(currentDomain)) {
      return false;
    }

    // Cek user type requirement
    if (item.requiredUserType && userType !== item.requiredUserType) {
      return false;
    }

    // Cek access level requirement
    if (item.requiredAccessLevel && accessLevel !== item.requiredAccessLevel) {
      return false;
    }

    // Cek apakah route diizinkan di domain saat ini
    if (!this.domainService.isRouteAllowed(item.route, accessLevel)) {
      return false;
    }

    return true;
  }

  navigateTo(route: string): void {
    const accessLevel = this.authService.getAccessLevel();
    if (this.domainService.isRouteAllowed(route, accessLevel)) {
      this.router.navigate([route]);
    } else {
      console.warn(`Route ${route} is not allowed in current domain`);
    }
  }

  navigateToExternalDomain(userType: UserRoleType): void {
    const redirectUrl = this.domainService.getRedirectUrl(userType);
    window.location.href = redirectUrl;
  }

  getDefaultRoute(): string {
    const currentDomain = this.domainService.getCurrentDomain();
    const userType = this.authService.getUserType();
    
    switch (currentDomain) {
      case 'login':
        return '/login';
      case 'join':
        return '/register';
      case 'dsp':
        return (userType === 'Advertisers' || userType === 'Operator') ? '/main/campaign' : '/main/welcome';
      case 'ssp':
        return (userType === 'Media Owners' || userType === 'Operator') ? '/main/inventory' : '/main/welcome';
      default:
        return '/main/welcome';
    }
  }

  canNavigateToRoute(route: string): boolean {
    const accessLevel = this.authService.getAccessLevel();
    return this.domainService.isRouteAllowed(route, accessLevel);
  }

  getAvailableRoutes(): string[] {
    const navigationItems = this.getNavigationItems();
    const routes: string[] = [];
    
    const extractRoutes = (items: NavigationItem[]) => {
      items.forEach(item => {
        routes.push(item.route);
        if (item.children) {
          extractRoutes(item.children);
        }
      });
    };
    
    extractRoutes(navigationItems);
    return routes;
  }

  getBreadcrumbs(currentRoute: string): {label: string, route?: string}[] {
    const breadcrumbs: {label: string, route?: string}[] = [];
    const pathSegments = currentRoute.split('/').filter(segment => segment);
    
    // Add Home
    breadcrumbs.push({ label: 'Home', route: '/main/welcome' });
    
    // Build breadcrumbs from path segments
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      const item = this.findNavigationItem(currentPath);
      if (item) {
        breadcrumbs.push({
          label: item.label,
          route: index === pathSegments.length - 1 ? undefined : currentPath
        });
      }
    });
    
    return breadcrumbs;
  }

  private findNavigationItem(route: string): NavigationItem | null {
    const findInItems = (items: NavigationItem[]): NavigationItem | null => {
      for (const item of items) {
        if (item.route === route) {
          return item;
        }
        if (item.children) {
          const found = findInItems(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findInItems(this.baseNavigationItems);
  }
}
